import React, { useState } from 'react'
import { GOOGLE_DOC_MODAL } from '@chegg-wt/core/state/modals/constants'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { Modal } from '..'
import * as Styled from './styled'
import ErrorScreen from './ErrorScreen'

export const GOOGLE_DOC_MODAL_ID = 'google-doc-modal'
export const GOOGLE_DOC_CANCEL_ID = 'google-doc-cancel'
export const GOOGLE_DOC_SUBMIT_ID = 'google-doc-submit'

interface Props {
  isSignedIntoGoogle: boolean
  googleAuthResponse: {
    url?: string
    error?: string
  }
  errorCopy: {
    header: string
    description: string
    link: string
    href: string
  }
  closeModal: (modalName: string) => void
  exportToGoogle: () => void
}

export const CONNECT_GOOGLE_DRIVE_COPY = 'Connect Google Drive'
export const CONNECTED_GOOGLE_DRIVE_COPY = 'Account connected'
export const EXPORT_GOOGLE_DRIVE_COPY = 'Export to Google Docs'
export const COMPLETED_HEADER_COPY = 'Hooray!'
export const CONNECT_EXPORT_DESCRIPTION_COPY =
  'Connect your account to export citation list to Google Docs.'
export const CONNECTED_EXPORT_DESCRIPTION_COPY = 'Click to export your citation list.'
export const COMPLETED_DESCRIPTION_COPY =
  'Your citation list was exported to Google Docs.'
export const SUBMIT_BUTTON_CONNECT_COPY = 'Connect Account'
export const SUBMIT_BUTTON_EXPORT_COPY = 'Export'
export const SUBMIT_BUTTON_TRY_COPY = 'Try again'
export const CANCEL_BUTTON_COPY = 'Cancel'
export const SUBMIT_BUTTON_COMPLETED_COPY = 'View document'

export const GoogleDocModal: React.FC<Props> = ({
  isSignedIntoGoogle,
  googleAuthResponse,
  errorCopy,
  closeModal,
  exportToGoogle,
}) => {
  const [isExporting, setExporting] = useState(false)

  const handleExport = () => {
    if (isSignedIntoGoogle) {
      setExporting(true)
    }
    exportToGoogle()
  }

  const hasError = googleAuthResponse && googleAuthResponse.error
  const isComplete = googleAuthResponse && googleAuthResponse.url

  let headerCopy = CONNECT_GOOGLE_DRIVE_COPY
  let descriptionCopy = CONNECT_EXPORT_DESCRIPTION_COPY
  let submitCopy = SUBMIT_BUTTON_CONNECT_COPY

  if (isSignedIntoGoogle) {
    headerCopy = CONNECTED_GOOGLE_DRIVE_COPY
    descriptionCopy = CONNECTED_EXPORT_DESCRIPTION_COPY
    submitCopy = SUBMIT_BUTTON_EXPORT_COPY
  }

  if (isExporting) {
    headerCopy = EXPORT_GOOGLE_DRIVE_COPY
  }

  if (isComplete) {
    if (isExporting) {
      setExporting(false)
    }
    headerCopy = COMPLETED_HEADER_COPY
    descriptionCopy = COMPLETED_DESCRIPTION_COPY
    submitCopy = SUBMIT_BUTTON_COMPLETED_COPY
  }

  if (hasError) {
    if (isExporting) {
      setExporting(false)
    }
    submitCopy = SUBMIT_BUTTON_TRY_COPY
  }

  return (
    <Modal
      showModal
      closeButton
      outsideClick
      onClose={() => closeModal(GOOGLE_DOC_MODAL)}
    >
      <Styled.Container data-test={GOOGLE_DOC_MODAL_ID}>
        <Styled.Header>{headerCopy}</Styled.Header>
        {isExporting && <LoadingDots message="Exporting citations" />}
        {!isExporting && (
          <Styled.DescriptionContainer>
            <span>{descriptionCopy}</span>
          </Styled.DescriptionContainer>
        )}
        {hasError && <ErrorScreen copy={errorCopy} />}
        {!isExporting && (
          <Styled.CTAContainer>
            {!isComplete && (
              <>
                <Styled.CancelButton
                  size="medium"
                  onClick={() => closeModal(GOOGLE_DOC_MODAL)}
                  data-test={GOOGLE_DOC_CANCEL_ID}
                >
                  {CANCEL_BUTTON_COPY}
                </Styled.CancelButton>
                <Styled.SubmitButton
                  size="medium"
                  onClick={handleExport}
                  data-test={GOOGLE_DOC_SUBMIT_ID}
                >
                  {submitCopy}
                </Styled.SubmitButton>
              </>
            )}
            {isComplete && (
              <Styled.SubmitButton
                as="a"
                size="medium"
                href={googleAuthResponse.url}
                target="_blank"
                data-test={GOOGLE_DOC_SUBMIT_ID}
              >
                {submitCopy}
              </Styled.SubmitButton>
            )}
          </Styled.CTAContainer>
        )}
      </Styled.Container>
    </Modal>
  )
}
